// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { CSSProperties } from 'react';

const colors = {
  bg: '#FAFAF9',
  textPrimary: '#1C1917',
  textSecondary: '#78716C',
  green: '#00D2AA',
  blue: '#455391',
  cardBg: '#FFFFFF',
  cardBorder: '#E7E5E4',
  tagBg: '#F5F5F4',
  fontG42: 'G42 Sans'
} as const;

export const root: CSSProperties = {
  width: '100%',
  minWidth: 0,
  margin: 0,
  padding: '0 0 24px 0',
  boxSizing: 'border-box',
  fontFamily: colors.fontG42
};

export const sectionHeader: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 24,
  marginBottom: 24,
  flexWrap: 'wrap'
};

export const sectionHeaderLeft: CSSProperties = {
  flex: '1 1 400px'
};

export const sectionLabelRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 8
};

export const sectionLabelLine: CSSProperties = {
  width: 24,
  height: 3,
  backgroundColor: colors.green,
  borderRadius: 2
};

export const sectionLabelText: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.1em',
  color: colors.textSecondary,
  margin: 0,
  textTransform: 'uppercase'
};

export const sectionTitle: CSSProperties = {
  fontFamily: colors.fontG42,
  fontSize: 28,
  fontWeight: 900,
  color: colors.textPrimary,
  margin: '0 0 12px 0',
  lineHeight: 1.3
};

export const sectionSubtitle: CSSProperties = {
  fontSize: 16,
  fontWeight: 400,
  color: colors.textSecondary,
  lineHeight: 1.5,
  margin: 0
};

export const exportButton: CSSProperties = {
  padding: '12px 20px',
  borderRadius: 24,
  backgroundColor: colors.tagBg,
  border: '1px solid #E7E5E4',
  color: colors.textPrimary,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0
};

export const cardsRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(1, 1fr)',
  gap: 24,
  marginBottom: 48
};

export const actionCard: CSSProperties = {
  backgroundColor: colors.cardBg,
  border: `1px solid ${colors.cardBorder}`,
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 220
};

export const cardTopRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 16
};

export const cardIconWrap: CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export const cardTag: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 20,
  backgroundColor: colors.tagBg,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: colors.textSecondary,
  textTransform: 'uppercase',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6
};

export const cardType: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: colors.textSecondary,
  marginBottom: 8,
  textTransform: 'uppercase'
};

export const cardTitle: CSSProperties = {
  fontFamily: colors.fontG42,
  fontSize: 18,
  fontWeight: 700,
  color: colors.textPrimary,
  margin: '0 0 12px 0',
  lineHeight: 1.3
};

export const cardDesc: CSSProperties = {
  fontSize: 14,
  fontWeight: 400,
  color: colors.textSecondary,
  lineHeight: 1.5,
  margin: 0,
  flex: 1
};

export const exploreButton: CSSProperties = {
  marginTop: 20,
  padding: '10px 16px',
  borderRadius: 24,
  backgroundColor: colors.tagBg,
  border: '1px solid #E7E5E4',
  color: colors.textPrimary,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: '0.02em',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  textDecoration: 'none',
  fontFamily: 'G42 Sans'
};

export const sequenceSection: CSSProperties = {
  marginTop: 20
};

export const sequenceTitle: CSSProperties = {
  fontFamily: colors.fontG42,
  fontSize: 18,
  fontWeight: 700,
  color: colors.textPrimary,
  margin: '0 0 20px 0'
};

export const sequenceRow: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 12,
  flexWrap: 'wrap'
};

export const stepCapsule: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '14px 20px',
  borderRadius: 24,
  border: '2px solid',
  minWidth: '90%',
  boxSizing: 'border-box',
  height: '100%',
  flex: 1
};

export const stepNumber: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  fontWeight: 700,
  color: colors.cardBg,
  flexShrink: 0
};

export const stepLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  marginBottom: 2
};

export const stepName: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: colors.textPrimary,
  margin: 0
};

export const arrowConnector: CSSProperties = {
  color: colors.textSecondary,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center'
};

export const backLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 20,
  fontSize: 14,
  fontWeight: 500,
  color: colors.textSecondary,
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  padding: 0
};

export const loadingMessage: CSSProperties = {
  textAlign: 'center',
  padding: 48,
  color: colors.textSecondary,
  fontSize: 16
};

export const errorMessage: CSSProperties = {
  ...loadingMessage,
  color: '#B91C1C'
};

export const featuredCard: CSSProperties = {
  backgroundColor: colors.cardBg,
  border: `2px solid ${colors.green}`,
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
  boxShadow: '0 4px 12px rgba(0,210,170,0.12)'
};

export const featuredHeader: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
  flexWrap: 'wrap',
  gap: 12
};

export const featuredBadge: CSSProperties = {
  padding: '8px 16px',
  borderRadius: 24,
  backgroundColor: `${colors.green}18`,
  color: colors.green,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase'
};

export const featuredRoleInfo: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: colors.textSecondary,
  letterSpacing: '0.02em'
};

export const featuredTitle: CSSProperties = {
  fontFamily: colors.fontG42,
  fontSize: 24,
  fontWeight: 700,
  color: colors.textPrimary,
  margin: '0 0 16px 0',
  lineHeight: 1.3
};

export const featuredPlatform: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 16,
  fontSize: 14,
  fontWeight: 600,
  color: colors.blue
};

export const featuredReasoning: CSSProperties = {
  fontSize: 15,
  fontWeight: 400,
  color: colors.textSecondary,
  lineHeight: 1.6,
  margin: '0 0 4px 0'
};

export const featuredButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 24px',
  borderRadius: 24,
  backgroundColor: colors.green,
  color: '#FFFFFF',
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: '0.02em',
  textDecoration: 'none',
  cursor: 'pointer',
  border: 'none',
  transition: 'all 0.2s ease'
};
